import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { UserContext } from '../../utils/contexts/UserContext';

export default function UniversityProfileWidget({
  university,
  isBackButton = false,
}) {
  const { user, isUniversity, isAdmin } = useContext(UserContext);
  const navigate = useNavigate();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 md:p-10">
      {university?.imageUrl && (
        <img
          src={university?.imageUrl}
          alt={university?.name}
          className="w-full h-64 object-cover rounded-lg mb-6 shadow-md"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src =
              'https://placehold.co/800x400/CCCCCC/000?text=Image+Not+Found';
          }}
        />
      )}
      <h2 className="text-4xl md:text-5xl font-extrabold mb-4">
        {university?.name} ({university?.short_name})
      </h2>
      {/* Key Information Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">
            Location:
          </p>
          <p className="text-xl text-gray-800 dark:text-gray-200">
            {university?.city}, {university?.state}, {university?.country}
          </p>
        </div>
        <div>
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">
            Founded Year:
          </p>
          <p className="text-xl text-gray-800 dark:text-gray-200">
            {university?.founded_year}
          </p>
        </div>
        <div>
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">
            Type:
          </p>
          <p className="text-xl text-gray-800 dark:text-gray-200">
            {university?.type?.name}
          </p>
        </div>
        {university?.ranking && (
          <div>
            <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">
              World Ranking:
            </p>
            <p className="text-xl text-gray-800 dark:text-gray-200">
              #{university?.ranking}
            </p>
          </div>
        )}
      </div>
      {/* Description */}
      <h3
        className="text-2xl font-bold mb-3
    text-gray-900 dark:text-white"
      >
        About
      </h3>
      <p
        className="text-lg leading-relaxed mb-6
    text-gray-700 dark:text-gray-300"
      >
        {university?.description}
      </p>

      <div className="grid grid-cols-2 gap-3 border-y border-gray-200 py-5 dark:border-gray-700 md:grid-cols-4">
        {[
          ['Interviews', university?.interview_count || 0],
          ['Questions', university?.total_questions || 0],
          ['Estimated minutes', university?.estimated_duration_minutes || 0],
          ['Covered skills', university?.covered_skills_count || 0],
        ].map(([label, value]) => (
          <div key={label} className="rounded-xl bg-gray-100 p-3 text-center dark:bg-gray-900">
            <strong className="block text-2xl">{value}</strong>
            <span className="text-xs text-gray-500">{label}</span>
          </div>
        ))}
      </div>
      <div className="grid gap-5 border-b border-gray-200 py-5 dark:border-gray-700 md:grid-cols-2">
        <div>
          <h3 className="font-semibold">Skills covered by active interviews</h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {university?.covered_skills?.length ? university.covered_skills.map((skill) => (
              <span key={skill.uuid} className="rounded-full bg-purple-50 px-3 py-1 text-sm text-purple-700 dark:bg-purple-950 dark:text-purple-200">{skill.name}</span>
            )) : <p className="text-sm text-gray-500">No managed skills are currently attached.</p>}
          </div>
        </div>
        <div>
          <h3 className="font-semibold">Availability and your progress</h3>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{university?.next_deadline ? `Next interview deadline: ${new Date(university.next_deadline).toLocaleString()}` : 'No upcoming deadline is currently set.'}</p>
          {university?.student_progress && <div className="mt-3"><div className="flex justify-between text-sm"><span>{university.student_progress.completed} completed · {university.student_progress.in_progress} in progress · {university.student_progress.remaining} remaining</span><strong>{university.student_progress.completion_percent}%</strong></div><div className="mt-1 h-2 overflow-hidden rounded-full bg-gray-200"><div className="h-full rounded-full bg-green-500" style={{ width: `${university.student_progress.completion_percent}%` }} /></div></div>}
        </div>
      </div>
      {/* Contact Information */}
      <h3
        className="text-2xl font-bold mb-3
    text-gray-900 dark:text-white"
      >
        Contact & Address
      </h3>
      <div className="text-lg mb-6">
        <p className="text-gray-700 dark:text-gray-300">
          <span className="font-semibold">Email:</span>{' '}
          <a
            href={`mailto:${university?.email}`}
            className="text-blue-600 hover:underline dark:text-blue-400"
          >
            {university?.email}
          </a>
        </p>
        <p className="text-gray-700 dark:text-gray-300">
          <span className="font-semibold">Website:</span>{' '}
          <a
            href={university?.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline dark:text-blue-400"
          >
            {university?.website}
          </a>
        </p>
        <p className="text-gray-700 dark:text-gray-300">
          <span className="font-semibold">Address:</span> {university?.address},{' '}
          {university?.city}, {university?.state}, {university?.postal_code},{' '}
          {university?.country}
        </p>
      </div>{' '}
      <div className="flex flex-row justify-end gap-4">
        {isBackButton && (
          <button
            onClick={() => navigate('/organizations')}
            className="mt-6 py-3 px-6 rounded-lg font-semibold transition duration-300 ease-in-out
              bg-purple-600 hover:bg-purple-700 text-white shadow-md
              focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-75"
          >
            Back to List
          </button>
        )}
        {(isAdmin ||
          (isUniversity && user.university?.uuid === university?.uuid)) && (
          <button
            onClick={() => navigate(`/universities/${university?.uuid}/edit`)}
            className="mt-6 py-3 px-6 rounded-lg font-semibold transition duration-300 ease-in-out
              bg-purple-600 hover:bg-purple-700 text-white shadow-md
              focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-75"
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
}
